"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import {
  userServices,
  Personel,
  UpdatePersonelRequest,
  Profile,
} from "@/app/services/userServices";
import { jabatanMasterServices } from "@/app/services/master/jabatanMasterServices";

// Pesan error yang ramah, deteksi timeout secara khusus
const getErrorMessage = (err: unknown, fallback: string): string => {
  if (typeof err === "object" && err !== null) {
    const e = err as { code?: string; message?: string };
    if (e.code === "ECONNABORTED" || /timeout/i.test(e.message ?? "")) {
      return "Server tidak merespons (timeout). Periksa koneksi atau coba lagi.";
    }
  }
  return err instanceof Error ? err.message : fallback;
};

export const useProfile = () => {
  const [profileData, setProfileData] = useState<Personel | null>(null);
  const [profileUsersData, setProfileUsersData] = useState<Profile | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState<boolean>(false);
  const [isPhotoLoading, setIsPhotoLoading] = useState<boolean>(false);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [jabatanList, setJabatanList] = useState<string[]>([]);
  const [isJabatanLoading, setIsJabatanLoading] = useState<boolean>(false);
  const [jabatanError, setJabatanError] = useState<string | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  // Clean up blob URL
  const revokeBlobUrl = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      revokeBlobUrl();
    };
  }, [revokeBlobUrl]);

  // Fetch personel data
  const fetchPersonel = useCallback(async () => {
    setIsProfileLoading(true);
    setProfileError(null);
    try {
      const data = await userServices.getCurrentPersonel();
      setProfileData(data);
      return data;
    } catch (err: unknown) {
      setProfileError(getErrorMessage(err, "Terjadi kesalahan saat memuat profil"));
      throw err;
    } finally {
      setIsProfileLoading(false);
    }
  }, []);

  // Fetch profile data
  const fetchProfile = useCallback(async () => {
    setIsProfileLoading(true);
    setProfileError(null);
    try {
      const data = await userServices.getProfile();
      setProfileUsersData(data);
      return data;
    } catch (err: unknown) {
      setProfileError(getErrorMessage(err, "Terjadi kesalahan saat memuat profil"));
      throw err;
    } finally {
      setIsProfileLoading(false);
    }
  }, []);

  // Fetch daftar jabatan
  const fetchJabatan = useCallback(async () => {
    setIsJabatanLoading(true);
    setJabatanError(null);

    try {
      const res = await jabatanMasterServices.getAll();

      if (res.success) {
        setJabatanList(res.data ?? []);
      } else {
        setJabatanError(res.message ?? "Gagal mengambil data jabatan");
      }
    } catch (err: unknown) {
      setJabatanError(getErrorMessage(err, "Gagal mengambil data jabatan"));
    } finally {
      setIsJabatanLoading(false);
    }
  }, []);

  // Fetch photo
  const fetchPhoto = useCallback(async (id: string) => {
    if (!id) {
      setPhotoUrl(null);
      return;
    }

    setIsPhotoLoading(true);
    try {
      revokeBlobUrl(); // Clean up old blob URL
      const blobUrl = await userServices.getPersonelPhoto(id);

      if (blobUrl) {
        blobUrlRef.current = blobUrl;
        setPhotoUrl(blobUrl);
      } else {
        setPhotoUrl(null);
      }
    } catch (error) {
      console.error("Gagal memuat foto:", error);
      setPhotoUrl(null);
    } finally {
      setIsPhotoLoading(false);
    }
  }, [revokeBlobUrl]);

  // Update personel
  const update = useCallback(
    async (
      data: UpdatePersonelRequest,
      id?: string,
    ): Promise<{ success: boolean; message?: string }> => {
      const targetId = id ?? profileData?.id;
      if (!targetId) {
        const msg = "ID personel tidak ditemukan";
        setProfileError(msg);
        return { success: false, message: msg };
      }

      setIsUpdating(true);
      setProfileError(null);

      try {
        const res = await userServices.updatePersonel(targetId, data);

        if (res.success) {
          if (res.data) {
            setProfileData(res.data as Personel);
          } else {
            await fetchPersonel();
          }
          return { success: true, message: res.message };
        } else {
          const errorMsg = res.message || "Gagal memperbarui profil";
          setProfileError(errorMsg);
          return { success: false, message: errorMsg };
        }
      } catch (err: unknown) {
        const errorMsg = getErrorMessage(err, "Gagal memperbarui profil");
        setProfileError(errorMsg);
        return { success: false, message: errorMsg };
      } finally {
        setIsUpdating(false);
      }
    },
    [profileData?.id, fetchPersonel],
  );

  // Upload photo
  const uploadPhoto = useCallback(
    async (
      file: File,
      id?: string,
    ): Promise<{ success: boolean; message?: string }> => {
      const targetId = id ?? profileData?.id;
      if (!targetId) {
        const msg = "ID personel tidak ditemukan";
        setProfileError(msg);
        return { success: false, message: msg };
      }

      setIsUploadingPhoto(true);
      setProfileError(null);

      try {
        await userServices.updatePersonelPhoto(targetId, file);
        await fetchPhoto(targetId);
        return { success: true, message: "Foto berhasil diunggah" };
      } catch (err: unknown) {
        const message = getErrorMessage(err, "Gagal mengunggah foto");
        setProfileError(message);
        return { success: false, message };
      } finally {
        setIsUploadingPhoto(false);
      }
    },
    [profileData?.id, fetchPhoto],
  );

  // Change password
  const changePassword = useCallback(
    async (
      currentPassword: string,
      newPassword: string,
    ): Promise<{ success: boolean; message?: string }> => {
      setIsChangingPassword(true);
      setPasswordError(null);

      try {
        const res = await userServices.changePassword({
          current_password: currentPassword,
          new_password: newPassword,
        });

        if (!res.success) {
          const errorMsg = res.message || "Gagal mengubah password";
          setPasswordError(errorMsg);
          return { success: false, message: errorMsg };
        }

        return { success: true, message: res.message || "Password berhasil diubah" };
      } catch (err: unknown) {
        const errorMsg = getErrorMessage(err, "Gagal mengubah password");
        setPasswordError(errorMsg);
        return { success: false, message: errorMsg };
      } finally {
        setIsChangingPassword(false);
      }
    },
    [],
  );

  // Fetch photo when profileData changes — hanya kalau personel memang
  // punya photo_url, supaya tidak selalu tembak endpoint /photo yang pasti
  // 404 untuk personel yang belum pernah unggah foto.
  useEffect(() => {
    if (profileData?.id && profileData.photo_url) {
      fetchPhoto(profileData.id);
    } else {
      setPhotoUrl(null);
    }
  }, [profileData?.id, profileData?.photo_url, fetchPhoto]);

  // Load all data on mount
  const loadData = useCallback(async () => {
  // Jangan tembak endpoint terproteksi kalau belum login → hindari timeout sia-sia
  if (typeof window !== "undefined" && !localStorage.getItem("token")) {
    return;
  }
  try {
    await Promise.all([fetchPersonel(), fetchProfile(), fetchJabatan()]);
  } catch (error) {
    console.error("Error loading profile data:", error);
  }
}, [fetchPersonel, fetchProfile, fetchJabatan]);

// Muat saat mount (kalau token sudah ada), dan muat ULANG begitu event
// "auth-login" ditembak axiosInstance setelah login sukses. Tanpa ini,
// begitu AuthGuard redirect ke dashboard setelah login, data profil tetap
// kosong sampai user reload manual — karena effect ini sebelumnya cuma
// jalan sekali saat mount, sebelum token sempat ada.
useEffect(() => {
  loadData();

  const onLogin = () => loadData();
  const onExpired = () => {
    setProfileData(null);
    setProfileUsersData(null);
    setPhotoUrl(null);
    revokeBlobUrl();
  };
  window.addEventListener("auth-login", onLogin);
  window.addEventListener("session-expired", onExpired);
  return () => {
    window.removeEventListener("auth-login", onLogin);
    window.removeEventListener("session-expired", onExpired);
  };
}, [loadData, revokeBlobUrl]);

  return {
    profile: {
      data: profileData,
      users: profileUsersData,
      isLoading: isProfileLoading,
      isUpdating,
      isUploadingPhoto,
      isPhotoLoading,
      photoUrl,
      error: profileError,
      refetch: fetchPersonel,
      refetchProfile: fetchProfile,
      refetchPhoto: fetchPhoto,
      update,
      uploadPhoto,
      isChangingPassword,
      passwordError,
      changePassword,
    },
    jabatan: {
      data: jabatanList,
      isLoading: isJabatanLoading,
      error: jabatanError,
      refetch: fetchJabatan,
    },
  };
};
